import { Component, computed, Input, signal, Signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTrash, faUpload } from '@fortawesome/free-solid-svg-icons';
import { State } from 'src/app/components/designer/models/state';
import { Step } from 'src/app/components/designer/models/step';
import { FolderTrackingStepVisibilityCondition } from 'src/app/models/enums/folder-tracking-step-visibility-condition';
import { FolderTrackingStepCustomization } from 'src/app/models/folder-tracking-step-customization';

@Component({
    selector: 'app-step-customizations',
    imports: [MatTableModule, MatFormFieldModule, MatSelectModule, FormsModule, FontAwesomeModule],
    templateUrl: './step-customization.component.html',
    styleUrl: './step-customization.component.scss'
})
export class StepCustomizationsComponent {
    /**
     * Icones utilisées dans le template.
     */
    public uploadIcon = faUpload;
    public deleteIcon = faTrash;

    /**
     * Enumérations utilisées dans le template
     */
    public FolderTrackingStepVisibilityCondition = FolderTrackingStepVisibilityCondition;

    /**
     * Liste des personnalisation des étapes.
     */
    @Input()
    public stepCustomizations: WritableSignal<FolderTrackingStepCustomization[]> = signal([]);

    /**
     * Liste des étapes.
     */
    @Input()
    public steps: Signal<Step[]> = signal([]);

    /**
     * Liste des colonnes du tableau.
     */
    displayedColumns = ['stepName', 'visibility', 'states', 'icon'];

    /**
     * Tableau indiquant pour chaque ligne si le controle de selection des états est visible.
     */
    public stepStateInputVisibility: Signal<boolean[]> = computed(() => {
        if (this.stepCustomizations()) {
            return this.stepCustomizations().map((x) => x.visibilityCondition === FolderTrackingStepVisibilityCondition.OnlyIfNotInStates);
        } else {
            return null;
        }
    });

    /**
     * Tableau contenant pour chaque ligne la représentation en Base64 de l'icone.
     */
    public icons: Signal<string[]> = computed(() => {
        if (this.stepCustomizations()) {
            return this.stepCustomizations().map((x) => (x.icon ? `data:${x.iconMimeType};base64,${x.icon}` : null));
        } else {
            return null;
        }
    });

    /**
     * Appelée lorsque la visibilité d'une étape est modifiée.
     * @param event : Evenement emis par la liste déroulante.
     * @param stepId : Id de l'atepe concernée.
     */
    public onStepVisibilityChange(event: MatSelectChange, stepId: number) {
        // Met à jour le signal 'stepCustomizations' pour recalculer 'stepStateInputVisibility'
        let tab = this.stepCustomizations().slice();

        if (event.value !== FolderTrackingStepVisibilityCondition.OnlyIfNotInStates) {
            const step = tab.find((x) => x.stepId === stepId);
            step.states = [];
        }

        this.stepCustomizations.set(tab);
    }

    /**
     * Appelée lorsque l'utilisateur déplace une image au dessus du contrôle d'upload
     * @param uploader : Element HTML du contrôle d'upload
     */
    public onDragEnter(uploader: HTMLElement) {
        uploader.classList.add('drag');
    }

    /**
     * Appelée lorsque l'utilisateur déplace une image en dehors du contrôle d'upload
     * @param uploader : Element HTML du contrôle d'upload
     */
    public onDragLeave(uploader: HTMLElement) {
        uploader.classList.remove('drag');
    }

    /**
     * Appelée lorsque l'utilisateur dépose une image sur le contrôle d'upload
     * @param e : Evenement de dépose.
     * @param index : Index de la ligne concernée.
     * @param uploader : Element HTML du contrôle d'upload.
     */
    public onDrop(e, index: number, uploader: HTMLElement) {
        e.preventDefault();
        uploader.classList.remove('drag');
        this.loadIcon(e, index);
    }

    /**
     * Appelée lorsque l'utilisateur sélectionne une image avec le contrôle d'upload.
     */
    public onInputChange(e, index: number) {
        this.loadIcon(e, index);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton pour supprimer une icone.
     * @param index : Index de la ligne concernée.
     * @param fileInput : Element HTML de type input file.
     */
    public onRemoveIcon(index: number, fileInput: HTMLInputElement) {
        fileInput.value = ''; // Vide l'input de type file
        let tab = this.stepCustomizations().slice();
        tab[index].icon = null;
        tab[index].iconMimeType = null;
        this.stepCustomizations.set(tab);
    }

    /**
     * Retourne la liste des états d'une étape.
     * @param stepId : Id de l'étape.
     * @returns Une liste d'états.
     */
    public getStepStates(stepId: number): State[] {
        const step = this.steps().find((x) => x.id === stepId);
        return step ? step.data.states : [];
    }

    /**
     * Charge l'icone sélectionnée par l'utilisateur (par upload ou drag&drop).
     * @param e : Evenment de drop ou d'upload.
     * @param index : Index de la ligne concernée.
     */
    private loadIcon(e, index: number) {
        var file = e.dataTransfer ? e.dataTransfer.files[0] : e.target.files[0];

        var pattern = /image-*/;
        var reader = new FileReader();

        if (!file.type.match(pattern)) {
            alert('invalid format');
            return;
        }

        reader.onload = this.setStepIcon.bind(this, index);
        reader.readAsDataURL(file);
    }

    /**
     * Spécifie l'icone d'une étape
     * @param index Index de la ligne concernée.
     * @param e : Evenment de drop ou d'upload.
     */
    private setStepIcon(index: number, e) {
        var reader = e.target;

        let tab = this.stepCustomizations().slice();
        tab[index].icon = reader.result.split(',')[1];
        tab[index].iconMimeType = reader.result.split(';')[0].split(':')[1];
        this.stepCustomizations.set(tab);
    }
}
